import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
  User,
  Phone,
  Mail,
  DollarSign,
  Zap,
  Pencil,
  Wrench,
  FileText,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  ImagePlus,
  Lock,
  Package,
  Trash2,
  Settings2,
  ClipboardList,
  ShieldCheck,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPlan } from "@/hooks/useTenantPlan";
import { useTenantSettings } from "@/hooks/useTenantSettings";
import { useSolarConfig } from "@/hooks/useSolarConfig";
import {
  useQuickQuoteMutations,
  type QuickQuote,
  type QuoteLineItem,
} from "@/hooks/useQuickQuotes";
import { BRAZIL_STATES, fetchCitiesByState } from "@/lib/brazil-states";
import { formatPhone } from "@/lib/validators";
import {
  calculateSimplifiedDimensioning,
  type ConnectionType,
  type SolarDimensioning,
} from "@/lib/solar-calculator";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { QuickQuoteItemsTable } from "./QuickQuoteItemsTable";

// ─────────────── Validation schema ───────────────

const formSchema = z.object({
  client_name: z.string().min(2, "Nome obrigatório"),
  client_phone: z
    .string()
    .min(8, "Telefone obrigatório")
    .refine((v) => v.replace(/\D/g, "").length >= 10, {
      message: "Telefone precisa ter DDD + número (mín. 10 dígitos)",
    }),
  client_email: z.string().email("E-mail inválido").or(z.literal("")).optional(),
  client_type: z.enum(["residential", "commercial", "rural"]),
  client_state: z.string().optional(),
  client_city: z.string().optional(),
  calc_mode: z.enum(["simplified", "manual", "cpq"]),
  calc_basis: z.enum(["consumption", "target"]),
  // simplified
  bill_value: z.coerce.number().optional(),
  connection_type: z.string().optional(),
  cip_value: z.coerce.number().optional(),
  extra_kwh_target: z.coerce.number().optional(),
  // potência do painel utilizada no cálculo (snapshot por cotação)
  panel_watts_used: z.coerce.number().min(100).max(1500).optional(),
  // unidade do "aumento previsto" no modo Por objetivo (apenas UI; persiste em kWh em extra_kwh_target)
  extra_target_unit: z.enum(["kwh", "brl"]).optional(),
  // manual
  power_kwp: z.coerce.number().optional(),
  panel_count: z.coerce.number().int().optional(),
  estimated_generation_kwh: z.coerce.number().optional(),
  monthly_savings: z.coerce.number().optional(),
  payback_years: z.coerce.number().optional(),
  total_value: z.coerce.number().optional(),
  // commercial
  proposal_style: z.enum(["clean", "complete"]),
  // 0 = sentinela "sem validade definida" (persistido como expires_at NULL)
  validity_days: z.coerce.number().int().min(0),
  payment_observations: z.string().optional(),
  payment_condition: z
    .enum(["cash", "installments_2_5", "installments_6_12", "financing"])
    .nullable()
    .optional(),
  warranty_text: z.string().optional(),
  observations: z.string().optional(),
  internal_notes: z.string().optional(),
  bill_photo_url: z.string().optional(),
  // items + discount
  line_items: z.array(z.any()).optional(),
  discount_type: z.enum(["percent", "fixed"]).nullable().optional(),
  discount_value: z.coerce.number().min(0).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface QuickQuoteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote?: QuickQuote;
  defaultLeadId?: string;
  /** Pré-preenche campos do cliente quando criando nova cotação (ignorado em edição). */
  defaultClient?: {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    city?: string | null;
    state?: string | null;
  };
  onSaved?: (quote: QuickQuote) => void;
}

// ─────────────── Helpers ───────────────

function parseCurrencyInput(raw: string): number {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return 0;
  return parseFloat(digits) / 100;
}

function formatCurrencyDisplay(value: number | undefined | null): string {
  if (value == null || isNaN(value)) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

const STEPS = [
  { id: "client", label: "Cliente", labelShort: "Cliente", icon: User },
  { id: "system", label: "Sistema Solar", labelShort: "Sistema", icon: Zap },
  { id: "items", label: "Equipamentos", labelShort: "Itens", icon: Package },
  { id: "terms", label: "Condições", labelShort: "Condições", icon: ClipboardList },
  { id: "summary", label: "Resumo", labelShort: "Resumo", icon: CheckCircle2 },
] as const;

type StepId = (typeof STEPS)[number]["id"];

// ─────────────── City Combobox (busca case/acento-insensitive) ───────────────

function normalizeCity(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

function CityCombobox({
  value,
  onChange,
  cities,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  cities: string[];
  disabled?: boolean;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-10",
            !value && "text-muted-foreground",
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          // Mitiga conflito de focus trap quando o Popover abre dentro do Dialog.
          // O CommandInput ainda recebe foco via autoFocus interno do cmdk.
        }}
      >
        <Command
          filter={(itemValue, search) => {
            const n = normalizeCity(itemValue);
            const s = normalizeCity(search);
            return n.includes(s) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Buscar cidade..." className="h-9" />
          <div className="max-h-[280px] overflow-y-auto overscroll-contain">
            <CommandEmpty className="py-6 text-center text-sm">
              Nenhuma cidade encontrada.
            </CommandEmpty>
            <CommandGroup>
              {cities.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === c ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RestoreDefaultsButton({
  hasAnyDefault,
  onConfirm,
}: {
  hasAnyDefault: boolean;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          disabled={!hasAnyDefault}
          title={
            hasAnyDefault
              ? "Restaurar campos desta aba com os padrões do tenant"
              : "Nenhum padrão configurado em Configurações → Cotação Rápida"
          }
        >
          <ShieldCheck className="h-3 w-3 mr-1.5" />
          Restaurar padrões
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Restaurar padrões?</AlertDialogTitle>
          <AlertDialogDescription>
            Isso substituirá as edições atuais desta aba com os padrões do
            tenant. Deseja continuar?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            Restaurar padrões
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─────────────── Component ───────────────

export function QuickQuoteFormDialog({
  open,
  onOpenChange,
  quote,
  defaultLeadId,
  defaultClient,
  onSaved,
}: QuickQuoteFormDialogProps) {
  const isEditing = !!quote;
  const isMobile = useIsMobile();
  const { currentTenant } = useAuth();
  const { hasFeature } = useTenantPlan();
  const { getSetting } = useTenantSettings();
  const { solarConfig, getIrradiationForState, configRow } = useSolarConfig();
  const { createQuote, updateQuote, generateSlug } = useQuickQuoteMutations();

  const isResettingRef = useRef(false);
  const [activeStep, setActiveStep] = useState<StepId>("client");
  const [leadIdFromPhone, setLeadIdFromPhone] = useState<string | null>(
    defaultLeadId ?? null,
  );
  const [duplicateInfo, setDuplicateInfo] = useState<{ name: string } | null>(
    null,
  );
  const [calcResult, setCalcResult] = useState<SolarDimensioning | null>(null);
  const [paybackEstimated, setPaybackEstimated] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [billPhotoPreview, setBillPhotoPreview] = useState<string | null>(null);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  // refs auxiliares: debounce do recálculo + rate-limit do toast de erro
  const recalcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCalcErrorAtRef = useRef<number>(0);

  // Modo CPQ removido nesta sprint (COTACAO-C.2). Cotações antigas com calc_mode='cpq' são
  // normalizadas para 'manual' na carga.
  const canUseFinancialAnalysis = hasFeature("cotacao_analise_financeira");

  const defaultValidity = useMemo(() => {
    const v = parseInt(getSetting("default_quote_validity_days", "7"), 10);
    return Number.isFinite(v) && v >= 0 ? v : 7;
  }, [getSetting]);

  const defaultWarranty = useMemo(
    () => getSetting("default_warranty_text", ""),
    [getSetting],
  );
  const defaultObservations = useMemo(
    () => getSetting("default_observations_for_client", ""),
    [getSetting],
  );
  const defaultPaymentTerms = useMemo(
    () => getSetting("default_payment_terms_text", ""),
    [getSetting],
  );
  const defaultInternalNotes = useMemo(
    () => getSetting("default_internal_notes", ""),
    [getSetting],
  );

  // Custo médio R$/kWp para estimativa de investimento (fallbacks de mercado 2025)
  const costPerKwpEstimate = useMemo(() => {
    const v = parseFloat(getSetting("cost_per_kwp_estimate", "2800"));
    return Number.isFinite(v) && v > 0 ? v : 2800;
  }, [getSetting]);
  const costPerKwpMin = useMemo(() => {
    const v = parseFloat(getSetting("cost_per_kwp_min", "2800"));
    return Number.isFinite(v) && v > 0 ? v : 2800;
  }, [getSetting]);
  const costPerKwpMax = useMemo(() => {
    const v = parseFloat(getSetting("cost_per_kwp_max", "3200"));
    return Number.isFinite(v) && v > 0 ? v : 3200;
  }, [getSetting]);

  // Carrega cidades de uma UF (IBGE)
  async function loadCitiesFor(uf: string | null | undefined) {
    if (!uf) {
      setCities([]);
      return;
    }
    const stateObj = BRAZIL_STATES.find((s) => s.uf === uf);
    if (!stateObj) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const list = await fetchCitiesByState(stateObj.ibgeCode);
      setCities(list);
    } finally {
      setLoadingCities(false);
    }
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      client_name: "",
      client_phone: "",
      client_email: "",
      client_type: "residential",
      client_state: "",
      client_city: "",
      calc_mode: "simplified",
      calc_basis: "consumption",
      bill_value: undefined,
      connection_type: "bifasico",
      cip_value: 0,
      extra_kwh_target: 0,
      panel_watts_used: undefined,
      extra_target_unit: "kwh",
      power_kwp: undefined,
      panel_count: undefined,
      estimated_generation_kwh: undefined,
      monthly_savings: undefined,
      payback_years: undefined,
      total_value: undefined,
      proposal_style: "clean",
      validity_days: 7,
      payment_observations: "",
      payment_condition: null,
      warranty_text: "",
      observations: "",
      internal_notes: "",
      bill_photo_url: "",
      line_items: [],
      discount_type: null,
      discount_value: 0,
    },
  });

  // Reset on open / when quote changes
  useEffect(() => {
    if (!open) return;
    isResettingRef.current = true;
    if (quote) {
      form.reset({
        client_name: quote.client_name ?? "",
        client_phone: quote.client_phone ?? "",
        client_email: quote.client_email ?? "",
        client_type: quote.client_type,
        client_state: quote.client_state ?? "",
        client_city: quote.client_city ?? "",
        // Normaliza modo CPQ legado para Manual (UI sem CPQ nesta sprint)
        calc_mode: quote.calc_mode === "cpq" ? "manual" : quote.calc_mode,
        calc_basis: quote.calc_basis ?? "consumption",
        bill_value: quote.bill_value ?? undefined,
        connection_type: quote.connection_type ?? "bifasico",
        cip_value: quote.cip_value ?? 0,
        extra_kwh_target: quote.extra_kwh_target ?? 0,
        panel_watts_used:
          (quote as any).panel_watts_used ?? solarConfig.panelWatts,
        extra_target_unit: "kwh",
        power_kwp: quote.power_kwp ?? undefined,
        panel_count: quote.panel_count ?? undefined,
        estimated_generation_kwh: quote.estimated_generation_kwh ?? undefined,
        monthly_savings: quote.monthly_savings ?? undefined,
        payback_years: quote.payback_years ?? undefined,
        total_value: quote.total_value ?? undefined,
        proposal_style: quote.proposal_style,
        validity_days: quote.validity_days,
        payment_observations: quote.payment_observations ?? "",
        payment_condition: quote.payment_condition ?? null,
        warranty_text: quote.warranty_text ?? "",
        observations: quote.observations ?? "",
        internal_notes: quote.internal_notes ?? "",
        bill_photo_url: quote.bill_photo_url ?? "",
        line_items: (quote.line_items as QuoteLineItem[]) ?? [],
        discount_type: quote.discount_type,
        discount_value: quote.discount_value ?? 0,
      });
      setLeadIdFromPhone(quote.lead_id);
      setBillPhotoPreview(quote.bill_photo_url ?? null);
    } else {
      const pcState = defaultClient?.state ?? "";
      form.reset({
        client_name: defaultClient?.name ?? "",
        client_phone: defaultClient?.phone ?? "",
        client_email: defaultClient?.email ?? "",
        client_type: "residential",
        client_state: pcState,
        client_city: defaultClient?.city ?? "",
        calc_mode: "simplified",
        calc_basis: "consumption",
        bill_value: undefined,
        connection_type: "bifasico",
        cip_value: 0,
        extra_kwh_target: 0,
        panel_watts_used: solarConfig.panelWatts,
        extra_target_unit: "kwh",
        power_kwp: undefined,
        panel_count: undefined,
        estimated_generation_kwh: undefined,
        monthly_savings: undefined,
        payback_years: undefined,
        total_value: undefined,
        proposal_style: "clean",
        validity_days: defaultValidity,
        payment_observations: defaultPaymentTerms,
        payment_condition: null,
        warranty_text: defaultWarranty,
        observations: defaultObservations,
        internal_notes: defaultInternalNotes,
        bill_photo_url: "",
        line_items: [],
        discount_type: null,
        discount_value: 0,
      });
      setLeadIdFromPhone(defaultLeadId ?? null);
      setBillPhotoPreview(null);
      // UF: usar defaultClient.state se fornecido; senão, UF padrão do tenant
      const effectiveUf = pcState || configRow?.default_state || "";
      if (effectiveUf) {
        if (!pcState) form.setValue("client_state", effectiveUf);
        loadCitiesFor(effectiveUf);
      } else {
        setCities([]);
      }
    }
    // Edição: carregar cidades da UF salva
    if (quote?.client_state) {
      loadCitiesFor(quote.client_state);
    }
    setDuplicateInfo(null);
    setCalcResult(null);
    setPaybackEstimated(false);
    setActiveStep("client");
    setTimeout(() => {
      isResettingRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quote?.id, defaultLeadId, defaultValidity, configRow?.default_state]);

  const watchedMode = form.watch("calc_mode");
  const watchedBasis = form.watch("calc_basis");
  const watchedStyle = form.watch("proposal_style");
  const watchedName = form.watch("client_name");
  const watchedPhone = form.watch("client_phone");
  const watchedItems = form.watch("line_items") as QuoteLineItem[] | undefined;
  const watchedDiscountType = form.watch("discount_type");
  const watchedDiscountValue = form.watch("discount_value") ?? 0;
  const watchedMonthlySavings = form.watch("monthly_savings");
  const watchedPowerKwp = form.watch("power_kwp");
  const watchedBillValue = form.watch("bill_value");
  const watchedExtraKwh = form.watch("extra_kwh_target");
  const watchedPanelWatts = form.watch("panel_watts_used");
  const watchedConnection = form.watch("connection_type");
  const watchedCip = form.watch("cip_value");
  const watchedState = form.watch("client_state");

  // Assinatura estável dos line_items para evitar disparos de recálculo a cada
  // re-render (useFieldArray retorna nova referência mesmo sem mudança real).
  // Inclui qty/preço/total — qualquer um afeta o investimento e portanto o payback.
  const itemsSignature = (watchedItems || [])
    .map(
      (it) =>
        `${it?.id || ""}:${Number(it?.quantity || 0)}:${Number(it?.unit_price || 0)}:${Number(it?.total || 0)}`,
    )
    .join("|");

  // Recálculo automático debounced (só após o vendedor ter clicado pelo menos uma vez em "Calcular")
  // Inclui line_items (assinatura estável) e desconto: payback precisa
  // recalcular sempre que o INVESTIMENTO muda, não só quando bill/consumo mudam.
  useEffect(() => {
    if (isResettingRef.current) return;
    if (watchedMode !== "simplified") return;
    if (!calcResult) return; // só re-roda depois do primeiro cálculo manual
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    recalcTimerRef.current = setTimeout(() => {
      handleCalculate({ silentError: true });
    }, 500);
    return () => {
      if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedBillValue,
    watchedExtraKwh,
    watchedPanelWatts,
    watchedConnection,
    watchedCip,
    watchedState,
    watchedBasis,
    itemsSignature,
    watchedDiscountType,
    watchedDiscountValue,
  ]);

  const normalizedWatchedPhone = (watchedPhone || "").replace(/\D/g, "");
  const canSubmit =
    !!watchedName?.trim() && normalizedWatchedPhone.length >= 10;

  // Derivação financeira automática (economia 25 anos com inflação 6%aa, CO2, árvores)
  useEffect(() => {
    if (isResettingRef.current) return;
    const ms = Number(watchedMonthlySavings) || 0;
    const kwp = Number(watchedPowerKwp) || 0;
    if (ms > 0) {
      const factor = (Math.pow(1.06, 25) - 1) / 0.06;
      const econ = ms * 12 * factor;
      // Não chamar setValue se não mudou, evita loops
      const current = form.getValues("monthly_savings");
      if (current === ms) {
        // ok
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMonthlySavings, watchedPowerKwp]);

  // Sincronizar total_value com line_items
  useEffect(() => {
    if (isResettingRef.current) return;
    const items = watchedItems || [];
    const itemsWithPrice = items.filter(
      (it) => Number(it?.unit_price || 0) > 0,
    );
    if (itemsWithPrice.length > 0) {
      const subtotal = items.reduce(
        (acc, it) => acc + Number(it?.total || 0),
        0,
      );
      let discountAmount = 0;
      if (watchedDiscountType === "percent" && watchedDiscountValue > 0) {
        discountAmount = (subtotal * watchedDiscountValue) / 100;
      } else if (watchedDiscountType === "fixed" && watchedDiscountValue > 0) {
        discountAmount = Math.min(subtotal, watchedDiscountValue);
      }
      const total = Math.max(0, subtotal - discountAmount);
      form.setValue("total_value", total, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedItems, watchedDiscountType, watchedDiscountValue]);

  // Phone duplicate check
  async function handlePhoneBlur() {
    const phone = form.getValues("client_phone");
    const normalized = (phone || "").replace(/\D/g, "");
    if (!normalized || normalized.length < 8 || !currentTenant) {
      setDuplicateInfo(null);
      setLeadIdFromPhone(null);
      return;
    }
    try {
      const { data, error } = await supabase.rpc("check_lead_duplicate", {
        p_tenant_id: currentTenant.id,
        p_phone_normalized: normalized,
      });
      if (error) {
        setLeadIdFromPhone(null);
        return;
      }
      const result = data as any;
      if (result?.duplicate && result?.field === "phone") {
        setDuplicateInfo({ name: result.existing_name });
        setLeadIdFromPhone(result.existing_id ?? null);
      } else {
        setDuplicateInfo(null);
        setLeadIdFromPhone(null);
      }
    } catch {
      setLeadIdFromPhone(null);
    }
  }

  function handleBillPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Tipo inválido", {
        description: "Apenas imagens ou PDF são aceitos.",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande", {
        description: "O limite é 5MB.",
      });
      return;
    }
    // Simplificação segura: armazenar em memória local até existir bucket dedicado
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setBillPhotoPreview(dataUrl);
      // bill_photo_url só armazenará URL real após upload; por ora deixamos vazio
      form.setValue("bill_photo_url", "", { shouldDirty: true });
      toast.info("Foto carregada localmente", {
        description: "O upload ao servidor será habilitado em breve.",
      });
    };
    reader.readAsDataURL(file);
  }

  // Calcula o valor do projeto a usar no payback (linha de itens > total manual > custo×kWp)
  function getProjectValueForCalc(currentKwp: number): {
    value: number;
    estimated: boolean;
  } {
    const items =
      (form.getValues("line_items") as QuoteLineItem[] | undefined) || [];
    const itemsWithPrice = items.filter(
      (it) => Number(it?.unit_price || 0) > 0,
    );
    if (itemsWithPrice.length > 0) {
      const subtotal = items.reduce(
        (acc, it) => acc + Number(it?.total || 0),
        0,
      );
      const dType = form.getValues("discount_type");
      const dValue = Number(form.getValues("discount_value")) || 0;
      let discount = 0;
      if (dType === "percent" && dValue > 0)
        discount = (subtotal * dValue) / 100;
      else if (dType === "fixed" && dValue > 0)
        discount = Math.min(subtotal, dValue);
      return { value: Math.max(0, subtotal - discount), estimated: false };
    }
    const totalManual = Number(form.getValues("total_value")) || 0;
    if (totalManual > 0) return { value: totalManual, estimated: false };
    if (currentKwp > 0 && costPerKwpEstimate > 0) {
      return { value: currentKwp * costPerKwpEstimate, estimated: true };
    }
    return { value: 0, estimated: false };
  }

  async function handleCalculate(opts?: { silentError?: boolean }) {
    const values = form.getValues();
    const billValue = values.bill_value || 0;
    if (!billValue) {
      if (!opts?.silentError) {
        toast.warning("Informe o valor da conta de luz para calcular");
      }
      return;
    }

    setIsCalculating(true);
    try {
      let irradiation = values.client_state
        ? getIrradiationForState(values.client_state)
        : null;
      if (!irradiation) {
        if (!values.client_state && !opts?.silentError) {
          toast.warning("Selecione o estado para cálculo mais preciso");
        }
        irradiation = 4.5;
      }

      // Potência do painel: snapshot por cotação (com fallback para config do tenant)
      const panelWatts =
        Number(values.panel_watts_used) > 0
          ? Number(values.panel_watts_used)
          : solarConfig.panelWatts;

      // Modo "Consumo atual": consumptionKwh = 0 (calculadora deriva da conta).
      // Modo "Por objetivo": adiciona kWh extras ao consumo derivado da conta.
      let consumptionKwh = 0;
      if (values.calc_basis === "target") {
        const baseKwh = billValue / (solarConfig.defaultTariff || 1);
        const extra = Number(values.extra_kwh_target || 0);
        consumptionKwh = baseKwh + extra;
      }

      // Pré-cálculo só pra estimar kWp (e então decidir o projectValue do payback)
      const preview = calculateSimplifiedDimensioning(
        consumptionKwh,
        panelWatts,
        irradiation,
        solarConfig.sizingMarginPct,
        billValue,
        solarConfig.defaultTariff,
        0,
        solarConfig,
        (values.connection_type as ConnectionType) || "bifasico",
        values.cip_value || 0,
      );
      const projectInfo = getProjectValueForCalc(preview.powerKwp);

      const result = calculateSimplifiedDimensioning(
        consumptionKwh,
        panelWatts,
        irradiation,
        solarConfig.sizingMarginPct,
        billValue,
        solarConfig.defaultTariff,
        projectInfo.value,
        solarConfig,
        (values.connection_type as ConnectionType) || "bifasico",
        values.cip_value || 0,
      );

      setCalcResult(result);
      setPaybackEstimated(projectInfo.estimated);
      form.setValue("power_kwp", result.powerKwp);
      form.setValue("panel_count", result.panelCount);
      form.setValue("estimated_generation_kwh", result.estimatedGenerationKwh);
      form.setValue("monthly_savings", result.monthlySavings);
      form.setValue("payback_years", result.paybackYears);
    } catch (err) {
      // Rate-limit: no máximo um toast de erro a cada 3s (evita spam no recálculo debounced)
      const now = Date.now();
      if (!opts?.silentError && now - lastCalcErrorAtRef.current > 3000) {
        lastCalcErrorAtRef.current = now;
        toast.error("Erro ao calcular", {
          description: (err as any)?.message || String(err),
        });
      }
    } finally {
      setIsCalculating(false);
    }
  }

  function buildPayload(values: FormValues): Partial<QuickQuote> {
    const normalized = (values.client_phone || "").replace(/\D/g, "");
    const items = (values.line_items as QuoteLineItem[]) || [];
    const itemsWithPrice = items.filter(
      (it) => Number(it?.unit_price || 0) > 0,
    );
    const subtotal =
      itemsWithPrice.length > 0
        ? items.reduce((acc, it) => acc + Number(it?.total || 0), 0)
        : null;

    // Derivação financeira
    const ms = Number(values.monthly_savings) || 0;
    const kwp = Number(values.power_kwp) || 0;
    const econ25 =
      ms > 0 ? ms * 12 * ((Math.pow(1.06, 25) - 1) / 0.06) : null;
    const co2 = kwp > 0 ? Math.round(kwp * 1233) : null;
    const trees = co2 != null ? Math.round(co2 / 150) : null;

    return {
      lead_id: leadIdFromPhone,
      client_name: values.client_name,
      client_phone: values.client_phone,
      client_phone_normalized: normalized,
      client_email: values.client_email || null,
      client_city: values.client_city || null,
      client_state: values.client_state || null,
      client_type: values.client_type,
      calc_mode: values.calc_mode,
      calc_basis: values.calc_basis,
      bill_value: values.bill_value ?? null,
      connection_type: values.connection_type ?? null,
      cip_value: values.cip_value ?? null,
      extra_kwh_target: values.extra_kwh_target ?? 0,
      panel_watts_used:
        Number(values.panel_watts_used) > 0
          ? Number(values.panel_watts_used)
          : null,
      power_kwp: values.power_kwp ?? null,
      panel_count: values.panel_count ?? null,
      estimated_generation_kwh: values.estimated_generation_kwh ?? null,
      monthly_savings: values.monthly_savings ?? null,
      payback_years: values.payback_years ?? null,
      economy_25_years: calcResult?.economy25Years ?? econ25 ?? null,
      co2_avoided_kg_year: calcResult?.co2AvoidedKgYear ?? co2 ?? null,
      trees_equivalent: calcResult?.treesEquivalent ?? trees ?? null,
      total_value: values.total_value ?? null,
      subtotal,
      line_items: items.length > 0 ? items : null,
      discount_type: values.discount_type ?? null,
      discount_value: values.discount_value ?? 0,
      payment_condition: values.payment_condition ?? null,
      warranty_text: values.warranty_text || null,
      observations: values.observations || null,
      internal_notes: values.internal_notes || null,
      bill_photo_url: values.bill_photo_url || null,
      proposal_style: values.proposal_style,
      include_financial_analysis: values.proposal_style === "complete",
      validity_days: values.validity_days,
      // Persiste expires_at já no salvar (não só ao gerar link).
      // validity_days === 0 → "Sem validade definida" → expires_at NULL.
      expires_at:
        values.validity_days > 0
          ? new Date(
              Date.now() + values.validity_days * 24 * 60 * 60 * 1000,
            ).toISOString()
          : null,
      payment_observations: values.payment_observations || null,
      status: "draft",
    };
  }

  async function handleSaveDraft() {
    if (!canSubmit) return;
    const valid = await form.trigger();
    if (!valid) {
      toast.warning("Corrija os campos destacados antes de salvar");
      return;
    }
    await handlePhoneBlur();
    const values = form.getValues();
    try {
      const payload = buildPayload(values);
      let saved: QuickQuote;
      if (isEditing && quote) {
        saved = await updateQuote.mutateAsync({ id: quote.id, data: payload });
      } else {
        saved = await createQuote.mutateAsync(payload);
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch {
      // já tratado
    }
  }

  async function handleSaveAndGenerateLink() {
    if (!canSubmit) return;
    const valid = await form.trigger();
    if (!valid) {
      toast.warning("Corrija os campos destacados antes de salvar");
      return;
    }
    await handlePhoneBlur();
    const values = form.getValues();
    try {
      const payload = buildPayload(values);
      let saved: QuickQuote;
      if (isEditing && quote) {
        saved = await updateQuote.mutateAsync({ id: quote.id, data: payload });
      } else {
        saved = await createQuote.mutateAsync(payload);
      }
      const slug = await generateSlug.mutateAsync({
        id: saved.id,
        code: saved.code,
        validityDays: values.validity_days,
      });
      const url = `${window.location.origin}/p/cotacao/${slug}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link gerado e copiado", { description: url });
      } catch {
        toast.success("Link gerado", { description: url });
      }
      onOpenChange(false);
      onSaved?.({
        ...saved,
        public_slug: slug,
        sent_at: saved.sent_at ?? new Date().toISOString(),
        status: "sent",
      });
    } catch {
      // já tratado
    }
  }

  // Step navigation
  const stepIndex = STEPS.findIndex((s) => s.id === activeStep);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  async function validateAndAdvance() {
    if (activeStep === "client") {
      const valid = await form.trigger(["client_name", "client_phone"]);
      if (!valid) return;
    }
    if (activeStep === "system" && watchedMode === "simplified") {
      if (!form.getValues("bill_value")) {
        toast.warning("Informe o valor da conta de luz");
        return;
      }
    }
    if (!isLastStep) {
      setActiveStep(STEPS[stepIndex + 1].id);
    }
  }

  function goBack() {
    if (!isFirstStep) setActiveStep(STEPS[stepIndex - 1].id);
  }

  const isSaving =
    createQuote.isPending || updateQuote.isPending || generateSlug.isPending;

  // ─── Render ───

  const dialogContentClass = isMobile
    ? "w-full h-full max-w-full rounded-none m-0 p-0 inset-0 translate-x-0 translate-y-0 left-0 top-0 flex flex-col gap-0"
    : "max-w-4xl w-full max-h-[90vh] p-0 flex flex-col gap-0 lg:max-w-4xl md:max-w-2xl";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(dialogContentClass)}>
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            {isEditing ? `Cotação ${quote?.code}` : "Nova Cotação Rápida"}
          </DialogTitle>
          <DialogDescription>
            Gere uma cotação compartilhável em poucos passos.
          </DialogDescription>
          {isMobile && (
            <div className="mt-2">
              <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{
                    width: `${((stepIndex + 1) / STEPS.length) * 100}%`,
                  }}
                />
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 flex items-center justify-between">
                <span>
                  Passo {stepIndex + 1} de {STEPS.length}
                </span>
                <span className="font-medium">
                  {STEPS[stepIndex].label}
                </span>
              </div>
            </div>
          )}
        </DialogHeader>

        {/* Body — form é o flex container que mantém TabsList sticky e o conteúdo rolando */}
        <Form {...form}>
          <form className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <Tabs
              value={activeStep}
              onValueChange={(v) => setActiveStep(v as StepId)}
              className="flex-1 min-h-0 flex flex-col"
            >
              {!isMobile && (
                <div className="px-6 pt-3 border-b shrink-0">
                  <TabsList className="grid grid-cols-5 w-full">
                    {STEPS.map((s) => {
                      const Icon = s.icon;
                      return (
                        <TabsTrigger
                          key={s.id}
                          value={s.id}
                          className="text-xs lg:text-sm"
                        >
                          <Icon className="h-3.5 w-3.5 mr-1.5 hidden lg:inline-block" />
                          <span className="hidden lg:inline">{s.label}</span>
                          <span className="inline lg:hidden">
                            {s.labelShort}
                          </span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                <div className="px-6 py-5 space-y-6">
                    {/* STEP 1 — Cliente */}
                    <TabsContent value="client" className="m-0 space-y-5">
                      <SectionHeader title="DADOS DO CLIENTE" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="client_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nome completo*</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <User className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                  <Input className="pl-9" {...field} />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="client_phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Telefone / WhatsApp*</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(formatPhone(e.target.value))
                                    }
                                    onBlur={() => {
                                      field.onBlur();
                                      handlePhoneBlur();
                                    }}
                                  />
                                </div>
                              </FormControl>
                              {duplicateInfo && (
                                <div className="text-xs text-warning flex items-start gap-1 mt-1">
                                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                  <span>
                                    Este número já pertence a{" "}
                                    <strong>{duplicateInfo.name}</strong>. A cotação será vinculada a este lead.
                                  </span>
                                </div>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="client_email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>E-mail</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Mail className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    type="email"
                                    {...field}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="client_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tipo de imóvel</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="residential">
                                    Residencial
                                  </SelectItem>
                                  <SelectItem value="commercial">
                                    Comercial
                                  </SelectItem>
                                  <SelectItem value="rural">Rural</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="client_state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Estado (UF)*</FormLabel>
                              <Select
                                value={field.value || "__none__"}
                                onValueChange={(v) => {
                                  const uf = v === "__none__" ? "" : v;
                                  field.onChange(uf);
                                  form.setValue("client_city", "");
                                  loadCitiesFor(uf);
                                }}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="__none__">—</SelectItem>
                                  {BRAZIL_STATES.map((s) => (
                                    <SelectItem key={s.uf} value={s.uf}>
                                      {s.uf} — {s.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="client_city"
                          render={({ field }) => {
                            const ufSelected = !!form.watch("client_state");
                            return (
                              <FormItem>
                                <FormLabel>Cidade</FormLabel>
                                <CityCombobox
                                  value={field.value || ""}
                                  onChange={field.onChange}
                                  cities={cities}
                                  disabled={!ufSelected || loadingCities}
                                  placeholder={
                                    !ufSelected
                                      ? "Selecione a UF primeiro"
                                      : loadingCities
                                        ? "Carregando..."
                                        : "Selecione a cidade"
                                  }
                                />
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      {/* Upload conta de luz */}
                      <div className="space-y-2">
                        <FormLabel>Foto da conta de luz (opcional)</FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Facilita o preenchimento dos dados.
                        </p>
                        <div className="flex items-start gap-3">
                          <label
                            className={cn(
                              "cursor-pointer flex flex-col items-center justify-center",
                              "border border-dashed rounded-lg p-4 hover:border-primary/50 transition-colors",
                              "text-xs text-muted-foreground",
                              "min-w-[120px] h-[100px]",
                            )}
                          >
                            <ImagePlus className="h-5 w-5 mb-1.5" />
                            <span>Anexar arquivo</span>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              className="hidden"
                              onChange={handleBillPhotoChange}
                            />
                          </label>
                          {billPhotoPreview && (
                            <div className="relative">
                              {billPhotoPreview.startsWith("data:image") ? (
                                <img
                                  src={billPhotoPreview}
                                  alt="Conta de luz"
                                  className="h-[100px] w-auto rounded-lg border object-cover"
                                />
                              ) : (
                                <div className="h-[100px] w-[120px] rounded-lg border flex items-center justify-center text-xs text-muted-foreground">
                                  PDF anexado
                                </div>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute -top-2 -right-2 h-6 w-6 bg-background border"
                                onClick={() => {
                                  setBillPhotoPreview(null);
                                  form.setValue("bill_photo_url", "");
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    {/* STEP 2 — Sistema */}
                    <TabsContent value="system" className="m-0 space-y-5">
                      <SectionHeader title="SISTEMA SOLAR" />
                      <div className="grid grid-cols-2 gap-3">
                        <ModeCard
                          icon={<Zap className="h-5 w-5" />}
                          title="Automático"
                          description="Só a conta de luz"
                          selected={watchedMode === "simplified"}
                          onClick={() =>
                            form.setValue("calc_mode", "simplified")
                          }
                        />
                        <ModeCard
                          icon={<Pencil className="h-5 w-5" />}
                          title="Manual"
                          description="Preencho tudo"
                          selected={watchedMode === "manual"}
                          onClick={() => form.setValue("calc_mode", "manual")}
                        />
                      </div>

                      {watchedMode === "simplified" && (
                        <div className="space-y-4">
                          {/* Sub-seletor: Consumo atual / Por objetivo */}
                          <Tabs
                            value={watchedBasis}
                            onValueChange={(v) =>
                              form.setValue(
                                "calc_basis",
                                v as "consumption" | "target",
                              )
                            }
                          >
                            <TabsList className="grid grid-cols-2 w-full max-w-md">
                              <TabsTrigger value="consumption">
                                <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                                Consumo atual
                              </TabsTrigger>
                              <TabsTrigger value="target">
                                <Settings2 className="h-3.5 w-3.5 mr-1.5" />
                                Por objetivo
                              </TabsTrigger>
                            </TabsList>
                          </Tabs>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="bill_value"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {watchedBasis === "target"
                                      ? "Conta de luz atual (R$)"
                                      : "Valor da conta de luz*"}
                                  </FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                      <Input
                                        className="pl-9"
                                        placeholder="R$ 0,00"
                                        value={formatCurrencyDisplay(field.value)}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseCurrencyInput(e.target.value),
                                          )
                                        }
                                      />
                                    </div>
                                  </FormControl>
                                  <p className="text-xs text-muted-foreground">
                                    {watchedBasis === "target"
                                      ? "Consumo atual de referência"
                                      : "Valor médio mensal"}
                                  </p>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {watchedBasis === "consumption" && (
                              <FormField
                                control={form.control}
                                name="connection_type"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Tipo de conexão</FormLabel>
                                    <Select
                                      value={field.value || "bifasico"}
                                      onValueChange={field.onChange}
                                    >
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="monofasico">
                                          Monofásico (30 kWh)
                                        </SelectItem>
                                        <SelectItem value="bifasico">
                                          Bifásico (50 kWh)
                                        </SelectItem>
                                        <SelectItem value="trifasico">
                                          Trifásico (100 kWh)
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                          </div>

                          {watchedBasis === "target" && (
                            <ExtraTargetBlock
                              form={form}
                              tariff={solarConfig.defaultTariff || 1}
                            />
                          )}

                          {/* Potência do painel — snapshot por cotação */}
                          <PanelWattsField
                            form={form}
                            defaultWatts={solarConfig.panelWatts}
                          />

                          <FormField
                            control={form.control}
                            name="cip_value"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  CIP — Iluminação pública (opcional)
                                </FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                      className="pl-9"
                                      placeholder="R$ 0,00"
                                      value={formatCurrencyDisplay(field.value)}
                                      onChange={(e) =>
                                        field.onChange(
                                          parseCurrencyInput(e.target.value),
                                        )
                                      }
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button
                            type="button"
                            onClick={() => handleCalculate()}
                            disabled={isCalculating}
                            className="w-full"
                          >
                            {isCalculating ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <BarChart3 className="h-4 w-4 mr-2" />
                            )}
                            Calcular estimativa
                          </Button>

                          {calcResult && (
                            <div className="rounded-lg border border-success/30 bg-success/10 p-4 space-y-3 animate-in fade-in slide-in-from-top-2">
                              <div className="flex items-center gap-2 text-success font-medium text-sm">
                                <CheckCircle2 className="h-4 w-4" />
                                Estimativa calculada
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-center">
                                <KpiBlock
                                  label="Potência"
                                  value={`${calcResult.powerKwp.toFixed(1)} kWp`}
                                />
                                <KpiBlock
                                  label="Painéis"
                                  value={`${calcResult.panelCount}`}
                                  hint={`de ${
                                    Number(form.getValues("panel_watts_used")) ||
                                    solarConfig.panelWatts
                                  }W`}
                                />
                                <KpiBlock
                                  label="Geração/mês"
                                  value={`${calcResult.estimatedGenerationKwh} kWh`}
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-center">
                                <KpiBlock
                                  label="Economia/mês"
                                  value={formatCurrencyDisplay(
                                    calcResult.monthlySavings,
                                  )}
                                />
                                <KpiBlock
                                  label="Payback"
                                  value={`${paybackEstimated ? "est. " : ""}${calcResult.paybackYears.toFixed(1)} anos${paybackEstimated ? " *" : ""}`}
                                />
                                <KpiBlock
                                  label="Economia 25 anos"
                                  value={formatCurrencyDisplay(
                                    calcResult.economy25Years,
                                  )}
                                />
                              </div>
                            </div>
                          )}

                          {/* Bloco de investimento estimado — fora do card verde para respiro visual */}
                          {calcResult &&
                            paybackEstimated &&
                            (calcResult.powerKwp || 0) > 0 && (
                              <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                                <p className="text-sm font-medium text-foreground">
                                  Investimento estimado:{" "}
                                  {formatCurrencyDisplay(
                                    calcResult.powerKwp * costPerKwpMin,
                                  )}{" "}
                                  a{" "}
                                  {formatCurrencyDisplay(
                                    calcResult.powerKwp * costPerKwpMax,
                                  )}
                                </p>
                                <div className="border-t pt-2">
                                  <p className="text-xs text-muted-foreground">
                                    * Estimado com base em R${" "}
                                    {costPerKwpMin.toLocaleString("pt-BR")}–R${" "}
                                    {costPerKwpMax.toLocaleString("pt-BR")}/kWp.
                                    Adicione equipamentos na aba Equipamentos
                                    para cálculo preciso.
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      )}

                      {watchedMode === "manual" && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <NumberField
                              name="power_kwp"
                              label="Potência (kWp)"
                              step={0.01}
                              form={form}
                            />
                            <NumberField
                              name="panel_count"
                              label="Número de painéis"
                              step={1}
                              form={form}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <NumberField
                              name="estimated_generation_kwh"
                              label="Geração mensal (kWh)"
                              step={1}
                              form={form}
                            />
                            <CurrencyField
                              name="monthly_savings"
                              label="Economia mensal"
                              form={form}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <NumberField
                              name="payback_years"
                              label="Payback (anos)"
                              step={0.1}
                              form={form}
                            />
                            <CurrencyField
                              name="total_value"
                              label="Investimento total"
                              form={form}
                              disabled={
                                (watchedItems || []).filter(
                                  (it) => Number(it?.unit_price || 0) > 0,
                                ).length > 0
                              }
                              hint={
                                (watchedItems || []).filter(
                                  (it) => Number(it?.unit_price || 0) > 0,
                                ).length > 0
                                  ? "Calculado pela tabela de itens"
                                  : "Preencha OU use a tabela de itens"
                              }
                            />
                          </div>
                        </div>
                      )}

                      {/* Modo CPQ removido nesta sprint (COTACAO-C.2) */}
                    </TabsContent>

                    {/* STEP 3 — Equipamentos */}
                    <TabsContent value="items" className="m-0 space-y-4">
                      <SectionHeader title="EQUIPAMENTOS" />
                      <p className="text-sm text-muted-foreground">
                        Opcional — use para detalhar os itens da cotação. O
                        total é calculado automaticamente.
                      </p>
                      <QuickQuoteItemsTable
                        items={(watchedItems as QuoteLineItem[]) || []}
                        onChange={(items) =>
                          form.setValue("line_items", items, {
                            shouldDirty: true,
                          })
                        }
                        discountType={watchedDiscountType ?? null}
                        discountValue={watchedDiscountValue}
                        onDiscountChange={(type, value) => {
                          form.setValue("discount_type", type, {
                            shouldDirty: true,
                          });
                          form.setValue("discount_value", value, {
                            shouldDirty: true,
                          });
                        }}
                      />
                    </TabsContent>

                    {/* STEP 4 — Condições */}
                    <TabsContent value="terms" className="m-0 space-y-5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <SectionHeader title="CONDIÇÕES COMERCIAIS" />
                        <RestoreDefaultsButton
                          hasAnyDefault={Boolean(
                            defaultWarranty ||
                              defaultObservations ||
                              defaultPaymentTerms ||
                              defaultInternalNotes,
                          )}
                          onConfirm={() => {
                            form.setValue("warranty_text", defaultWarranty, {
                              shouldDirty: true,
                            });
                            form.setValue("observations", defaultObservations, {
                              shouldDirty: true,
                            });
                            form.setValue(
                              "payment_observations",
                              defaultPaymentTerms,
                              { shouldDirty: true },
                            );
                            form.setValue(
                              "internal_notes",
                              defaultInternalNotes,
                              { shouldDirty: true },
                            );
                            toast.success(
                              "Campos restaurados com os padrões do tenant",
                            );
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="validity_days"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Validade da cotação</FormLabel>
                              <Select
                                value={String(field.value)}
                                onValueChange={(v) =>
                                  field.onChange(parseInt(v, 10))
                                }
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="7">7 dias</SelectItem>
                                  <SelectItem value="15">15 dias</SelectItem>
                                  <SelectItem value="30">30 dias</SelectItem>
                                  <SelectItem value="60">60 dias</SelectItem>
                                  <SelectItem value="0">Sem validade definida</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="payment_condition"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Condição de pagamento</FormLabel>
                              <Select
                                value={field.value ?? "__none__"}
                                onValueChange={(v) =>
                                  field.onChange(v === "__none__" ? null : (v as any))
                                }
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecionar..." />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="__none__">—</SelectItem>
                                  <SelectItem value="cash">À vista</SelectItem>
                                  <SelectItem value="installments_2_5">
                                    2 a 5x sem juros
                                  </SelectItem>
                                  <SelectItem value="installments_6_12">
                                    6 a 12x
                                  </SelectItem>
                                  <SelectItem value="financing">
                                    Financiamento
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="warranty_text"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Garantia (opcional)</FormLabel>
                            <FormControl>
                              <Textarea
                                rows={2}
                                placeholder="Ex: Módulos 25 anos | Inversor 10 anos | Instalação 1 ano"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="observations"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Observações para o cliente (opcional)
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                rows={3}
                                placeholder="Ex: Valores sujeitos a vistoria técnica."
                                {...field}
                              />
                            </FormControl>
                            <p className="text-xs text-muted-foreground">
                              Visível para o cliente
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="payment_observations"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Detalhes de pagamento (opcional)
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="Ex: Entrada de R$ 1.000 + 11x"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="internal_notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Notas internas (opcional)</FormLabel>
                            <FormControl>
                              <Textarea
                                rows={2}
                                placeholder="Anotações internas — não aparecem para o cliente"
                                {...field}
                              />
                            </FormControl>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Lock className="h-3 w-3" />
                              Apenas sua equipe vê
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </TabsContent>

                    {/* STEP 5 — Resumo */}
                    <TabsContent value="summary" className="m-0 space-y-5">
                      <SectionHeader title="REVISAR COTAÇÃO" />
                      <SummaryCard
                        values={form.getValues()}
                        itemsCount={(watchedItems || []).length}
                      />

                      <Separator />

                      <div>
                        <FormLabel>Estilo da proposta</FormLabel>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                          <ModeCard
                            icon={<FileText className="h-5 w-5" />}
                            title="Clean"
                            description="Objetiva e direta. Ideal para clientes que querem agilidade."
                            selected={watchedStyle === "clean"}
                            onClick={() =>
                              form.setValue("proposal_style", "clean")
                            }
                          />
                          <ModeCard
                            icon={<BarChart3 className="h-5 w-5" />}
                            title="Completa"
                            description="Com análise financeira, gráficos e comparativos."
                            selected={watchedStyle === "complete"}
                            disabled={!canUseFinancialAnalysis}
                            badge={
                              !canUseFinancialAnalysis
                                ? "Profissional+"
                                : undefined
                            }
                            onClick={() =>
                              canUseFinancialAnalysis &&
                              form.setValue("proposal_style", "complete")
                            }
                          />
                        </div>
                        {watchedStyle === "complete" &&
                          !watchedMonthlySavings && (
                            <div className="mt-2 text-xs text-warning flex items-start gap-1">
                              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>
                                A análise financeira requer Economia/mês
                                preenchida.
                              </span>
                            </div>
                          )}
                      </div>
                    </TabsContent>
                </div>
              </div>
            </Tabs>
          </form>
        </Form>

        {/* Footer */}
        <div
          className={cn(
            "px-4 sm:px-6 py-3 border-t bg-surface flex items-center gap-2 shrink-0",
            isMobile ? "justify-between" : "justify-between",
          )}
        >
          {isMobile ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={isFirstStep ? () => onOpenChange(false) : goBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {isFirstStep ? "Cancelar" : "Voltar"}
              </Button>
              {isLastStep ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSaveDraft}
                    disabled={!canSubmit || isSaving}
                  >
                    Rascunho
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveAndGenerateLink}
                    disabled={!canSubmit || isSaving}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-1" />
                    )}
                    Gerar link
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={validateAndAdvance}
                >
                  Avançar
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={goBack}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Anterior
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={!canSubmit || isSaving}
                >
                  {isSaving && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Salvar rascunho
                </Button>
                {!isLastStep ? (
                  <Button type="button" onClick={validateAndAdvance}>
                    Próximo
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSaveAndGenerateLink}
                    disabled={!canSubmit || isSaving}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    Salvar e gerar link
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Sub-components ───────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </h4>
      <Separator />
    </div>
  );
}

function ModeCard({
  icon,
  title,
  description,
  selected,
  onClick,
  disabled,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative rounded-lg border p-3 text-left transition-all hover:border-primary/50",
        selected && "border-primary ring-2 ring-primary/30 bg-primary/5",
        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
      )}
    >
      <div className="flex items-center gap-2 mb-1 text-foreground">
        {icon}
        <span className="font-medium text-sm">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      {badge && (
        <Badge className="absolute top-1.5 right-1.5 text-[10px] py-0 px-1.5">
          {badge}
        </Badge>
      )}
    </button>
  );
}

function KpiBlock({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold text-foreground">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

// ─────────────── PanelWattsField ───────────────

function PanelWattsField({
  form,
  defaultWatts,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  defaultWatts: number;
}) {
  const value = form.watch("panel_watts_used") ?? defaultWatts;
  const num = Number(value) || defaultWatts;
  const outOfRange = num < 300 || num > 800;
  return (
    <FormField
      control={form.control}
      name="panel_watts_used"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Potência do painel</FormLabel>
          <FormControl>
            <div className="relative max-w-[200px]">
              <Input
                type="number"
                min={100}
                max={1500}
                step={5}
                value={field.value ?? defaultWatts}
                onChange={(e) =>
                  field.onChange(Number(e.target.value) || defaultWatts)
                }
                className="pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                W
              </span>
            </div>
          </FormControl>
          {outOfRange ? (
            <p className="text-xs text-warning">
              Fora do range usual de mercado (300–800 W). Confira o valor.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Padrão do tenant: {defaultWatts}W. Editável só nesta cotação.
            </p>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ─────────────── ExtraTargetBlock ───────────────

function ExtraTargetBlock({
  form,
  tariff,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  tariff: number;
}) {
  const billValue = Number(form.watch("bill_value") || 0);
  const extraKwh = Number(form.watch("extra_kwh_target") || 0);
  const unit = (form.watch("extra_target_unit") as "kwh" | "brl") || "kwh";

  const baseConsumption = tariff > 0 ? Math.round(billValue / tariff) : 0;
  const totalConsumption = Math.round(baseConsumption + extraKwh);

  const inputValue =
    unit === "kwh"
      ? extraKwh
      : Math.round(extraKwh * tariff * 100) / 100;

  function handleInputChange(raw: string) {
    const num = Number(raw) || 0;
    if (unit === "kwh") {
      form.setValue("extra_kwh_target", num, { shouldDirty: true });
    } else {
      const kwh = tariff > 0 ? num / tariff : 0;
      form.setValue("extra_kwh_target", kwh, { shouldDirty: true });
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="text-muted-foreground">
          Tarifa: <span className="font-medium text-foreground">R$ {tariff.toFixed(2)}/kWh</span>
          <div className="text-[10px] mt-0.5 opacity-70">do tenant</div>
        </div>
        <div className="text-muted-foreground">
          → Consumo atual calculado:{" "}
          <span className="font-medium text-foreground">
            {baseConsumption.toLocaleString("pt-BR")} kWh/mês
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm">Aumento previsto</Label>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-input p-0.5 bg-background">
            <button
              type="button"
              onClick={() => form.setValue("extra_target_unit", "kwh")}
              className={cn(
                "px-3 py-1 text-xs rounded-sm transition-colors",
                unit === "kwh"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              kWh
            </button>
            <button
              type="button"
              onClick={() => form.setValue("extra_target_unit", "brl")}
              className={cn(
                "px-3 py-1 text-xs rounded-sm transition-colors",
                unit === "brl"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              R$
            </button>
          </div>
          <div className="relative flex-1 max-w-[220px]">
            <Input
              type="number"
              min={0}
              step={unit === "kwh" ? 1 : 0.01}
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              className="pr-12"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              {unit === "kwh" ? "kWh" : "R$"}
            </span>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Ex: 2 ACs novos = ~300 kWh/mês. Conversão automática usando a tarifa do tenant.
        </p>
      </div>

      <div className="rounded-md bg-background p-3 text-xs">
        → Consumo total a compensar:{" "}
        <span className="font-semibold text-foreground">
          {totalConsumption.toLocaleString("pt-BR")} kWh/mês
        </span>
      </div>
    </div>
  );
}

function NumberField({
  name,
  label,
  step,
  form,
}: {
  name: keyof FormValues;
  label: string;
  step: number;
  form: ReturnType<typeof useForm<FormValues>>;
}) {
  return (
    <FormField
      control={form.control}
      name={name as any}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              step={step}
              value={(field.value as number | undefined) ?? ""}
              onChange={(e) =>
                field.onChange(
                  e.target.value === "" ? undefined : Number(e.target.value),
                )
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function CurrencyField({
  name,
  label,
  form,
  disabled,
  hint,
}: {
  name: keyof FormValues;
  label: string;
  form: ReturnType<typeof useForm<FormValues>>;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name as any}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className="relative">
              <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="R$ 0,00"
                disabled={disabled}
                value={formatCurrencyDisplay(
                  field.value as number | undefined,
                )}
                onChange={(e) =>
                  field.onChange(parseCurrencyInput(e.target.value))
                }
              />
            </div>
          </FormControl>
          {hint && (
            <p className="text-xs text-muted-foreground">{hint}</p>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function SummaryCard({
  values,
  itemsCount,
}: {
  values: FormValues;
  itemsCount: number;
}) {
  const investmentLabel = values.total_value
    ? formatCurrencyDisplay(values.total_value)
    : "A definir";

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
      <SummaryRow
        label="Cliente"
        value={[values.client_name, values.client_phone, values.client_city]
          .filter(Boolean)
          .join(" • ")}
      />
      <SummaryRow
        label="Sistema"
        value={[
          values.calc_mode === "simplified" ? "Automático" : "Manual",
          values.power_kwp ? `${values.power_kwp} kWp` : null,
          values.panel_count ? `${values.panel_count} painéis` : null,
        ]
          .filter(Boolean)
          .join(" • ")}
      />
      <SummaryRow
        label="Estimativas"
        value={[
          values.monthly_savings
            ? `${formatCurrencyDisplay(values.monthly_savings)}/mês`
            : null,
          values.payback_years ? `Payback ${values.payback_years} anos` : null,
        ]
          .filter(Boolean)
          .join(" • ") || "—"}
      />
      <SummaryRow label="Investimento" value={investmentLabel} />
      {itemsCount > 0 && (
        <SummaryRow
          label="Itens"
          value={`${itemsCount} ${itemsCount === 1 ? "item listado" : "itens listados"}`}
        />
      )}
      <SummaryRow
        label="Validade"
        value={
          values.validity_days > 0
            ? `${values.validity_days} dias`
            : "Sem validade definida"
        }
      />
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground text-xs uppercase tracking-wider min-w-[100px]">
        {label}
      </span>
      <span className="font-medium text-right flex-1">{value || "—"}</span>
    </div>
  );
}
